import { motion } from "framer-motion";
import { Receipt, CreditCard, Unlock, Headset } from "lucide-react";
import AppMockup from "../../assets/imgs/phone_logo_svg.svg";
import QRCodeImg from "../../assets/imgs/qrcodeapp.svg";
import { FaGooglePlay, FaApple } from "react-icons/fa";

export default function AppBannerSection() {
  const features = [
    { icon: Receipt, text: "2ª Via de Fatura" },
    { icon: CreditCard, text: "Pagamento com Cartão" },
    { icon: Unlock, text: "Auto-desbloqueio" },
    { icon: Headset, text: "Central de Atendimento" }
  ];

  return (
    <section className="bg-primary relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 -mr-40 -mt-40 w-[600px] h-[600px] bg-white opacity-5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-40 -mb-40 w-[500px] h-[500px] bg-cyan-400 opacity-10 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-8 items-center">
          
          {/* Left: Text Content */}
          <div className="order-2 md:order-1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-poppins font-extrabold text-4xl sm:text-5xl lg:text-6xl mb-10 text-white leading-[1.1]">
                Resolva tudo no<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70">aplicativo da WebNorte</span>
              </h2>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 max-w-lg">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 p-4 rounded-2xl hover:bg-white/10 transition-colors">
                    <div className="bg-white/10 p-2.5 rounded-xl text-white">
                      <feature.icon className="w-5 h-5" />
                    </div>
                    <span className="font-semibold text-sm text-white/90">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Downloads & QR Code */}
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-8 border-t border-white/10 pt-8 max-w-xl">
                
                {/* Left: QR Code */}
                <div className="hidden sm:flex flex-col items-center gap-2 shrink-0">
                  <div className="bg-white p-2.5 rounded-2xl shadow-lg">
                    <img src={QRCodeImg} alt="QR Code" className="w-28 h-28 lg:w-32 lg:h-32 object-contain" />
                  </div>
                  <span className="text-[10px] text-white/60 font-bold uppercase tracking-wider">Escaneie aqui</span>
                </div>

                {/* Right: Buttons */}
                <div className="flex flex-col gap-3 w-full">
                  <p className="text-sm font-bold text-white/90 text-center sm:text-left">Ou baixe direto nas lojas:</p>
                  <div className="flex flex-col sm:flex-row xl:flex-col 2xl:flex-row gap-3">
                    <a 
                      href="https://play.google.com/store/apps/details?id=com.hubsoft_client_app.webnorte&hl=pt_BR" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-white text-foreground px-5 py-3 rounded-xl hover:bg-gray-100 transition-transform hover:scale-105 shadow-xl w-full sm:w-fit"
                    >
                      <FaGooglePlay className="w-6 h-6 text-[#3DDC84]" />
                      <div className="flex flex-col items-start">
                        <span className="text-[10px] font-medium leading-none mb-0.5 text-gray-500">Disponível no</span>
                        <span className="text-sm font-bold leading-none">Google Play</span>
                      </div>
                    </a>
                    
                    <a 
                      href="https://apps.apple.com/br/app/web-norte-telecom/id6747889812" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-3 bg-white text-foreground px-5 py-3 rounded-xl hover:bg-gray-100 transition-transform hover:scale-105 shadow-xl w-full sm:w-fit"
                    >
                      <FaApple className="w-6 h-6 text-black" />
                      <div className="flex flex-col items-start">
                        <span className="text-[10px] font-medium leading-none mb-0.5 text-gray-500">Baixar na</span>
                        <span className="text-sm font-bold leading-none">App Store</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

            </motion.div>
          </div>

          {/* Right: Phone Image */}
          <div className="order-1 md:order-2 flex justify-center items-center relative w-full">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="relative z-10 w-full max-w-[280px] sm:max-w-[320px] md:max-w-[380px] lg:max-w-[450px] mx-auto"
            >
              {/* Subtle background glow for the phone */}
              <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full scale-90 -z-10" />
              
              <img 
                src={AppMockup} 
                alt="Aplicativo WebNorte" 
                className="w-full h-auto object-contain drop-shadow-2xl relative z-10"
              />

            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}
